import React, { useEffect } from 'react';
import { Outlet, useParams, useSearchParams } from 'react-router-dom';
import { ReportContext } from '../../context/ReportProvider';
import { Container, Row, Col } from 'reactstrap';
import { reportsRoutesData } from './reportsDataSetup';
import { QSightPageHeader } from '@qsightnpm/qsight-component-library';
import { Link } from 'react-router-dom';
// import NavigationPanelDemo from '../../../NavigationPanelDemo/NavigationPanelDemo';
import './reportUtilityNav.css';
import { FileEarmarkText, Funnel, ArrowLeftCircleFill } from 'react-bootstrap-icons';
import NavigationPanelMenu from '../../NavigationPanel/NavigationPanelMenu';
import { store } from '../../../../stores/store';
import PinReport from '../../PinReport/PinReport';
import { getCurrDeptUserId } from '../../../../utils/redisCacheReader';
import useFetch from '../../hooks/useFetch';
import ReportFiltersDisplay from '../../FiltersDisplay/ReportFilterDisplay';
import { IMemorizedReportResponse } from './IMemorizedReportResponse';

const StandardReportLayout: React.FC = () => {
    let { reportId, view } = useParams();
    const [searchParams] = useSearchParams();
    const memId = searchParams.get('memid') || false;
    const qSightUserId: number | undefined = store.commonStore.redisCache ? getCurrDeptUserId(store.commonStore.redisCache as any) : undefined;
    const qSightHolderId: number | undefined = store.commonStore.departmentId ?? undefined;
    const baseUrl = store.commonStore.isRemote ? `/reporting/report/${reportId}` : `/report/${reportId}`;
    const redisKey = store.commonStore.redisKey;

    const reportTitle: string = reportId && reportsRoutesData[reportId] ? reportsRoutesData[reportId].title : 'Report Not Found';
    const qSightReportId: number | undefined = reportId && reportsRoutesData[reportId] ? reportsRoutesData[reportId].qSightReportId : undefined;
    const baseReportTitle: string = reportId && reportsRoutesData[reportId]
        ? `${reportsRoutesData[reportId].title} Report`
        : 'Report Not Found';

    const reportContext = React.useContext(ReportContext);
    const ReportComponent = reportId && reportsRoutesData[reportId] ? reportsRoutesData[reportId].Component : null;
    const DescriptionComponent = reportId && reportsRoutesData[reportId] ? reportsRoutesData[reportId].Description : null;

    const isDescription = view === 'description' && DescriptionComponent;

    const handleClick = (event: MouseEvent) => {
        const filtersDisplay = document.getElementById('filters-display-root');
        if (filtersDisplay && !filtersDisplay.contains(event.target as Node)) {
            reportContext?.setFiltersVisible(false);
        }
    };

    const reportResponse = useFetch<{
        report: IMemorizedReportResponse
    }>(memId ? `${process.env.REACT_APP_REPORT_API_URL}memorizedreportbyid?memorizedReportId=${memId}` : '');

    useEffect(() => {
        // Ensure provider knows which report is active for persistence
        if (reportContext?.setCurrentReportId) {
            reportContext.setCurrentReportId(reportId);
        }


        // Only hydrate filters once when a memorized report is loaded
        if (memId && reportResponse.data) {
            const parsed = reportResponse?.data?.report?.propertyValue
                ? JSON.parse(reportResponse.data.report.propertyValue)
                : {};

            // Avoid resetting filters if they already match memorized payload
            const current = reportContext?.reportFilter || {};
            const stringify = (o: any) => {
                try { return JSON.stringify(o); } catch { return ''; }
            };
            if (stringify(parsed) !== stringify(current)) {
                reportContext?.setReportFilter(parsed);
            }
            if (reportContext?.setMemorizedData) {
                const memCurrent = reportContext?.memorizedData || {};
                if (stringify(parsed) !== stringify(memCurrent)) {
                    reportContext.setMemorizedData(parsed);
                }
            }
        }

        // Note: do not depend on the entire reportContext object to prevent
        // reruns on unrelated context updates (which caused fetch loops).
    }, [reportId, memId, reportResponse?.data]);

    useEffect(() => {
        reportContext?.setReportNavVisible(!isDescription);
    }, [isDescription]);

    const toggleFilter = () => {
        reportContext?.setFiltersVisible(!reportContext.filtersVisible);
    };

    document.addEventListener('mousedown', handleClick);

    return (
        <Container fluid className="overflow-hidden">
            {ReportComponent ?
                <>
                    <QSightPageHeader
                        title={
                            memId
                                ? (reportResponse.loading
                                    ? 'Loading...'
                                    : (reportResponse?.data?.report?.savedName?.toString() || baseReportTitle))
                                : baseReportTitle
                        }
                        subtitle={
                            isDescription
                                ? ''
                                : (reportContext?.dataLastRefreshed && (reportContext?.reportTotalCount ?? 0) > 0
                                    ? `Data last refreshed: ${new Date(reportContext.dataLastRefreshed).toLocaleString()}`
                                    : '')
                        }
                        summary={
                            isDescription
                                ? reportId && reportsRoutesData[reportId]
                                    ? `${reportsRoutesData[reportId].title} Report Description`
                                    : 'Report Not Found'
                                : reportId && reportsRoutesData[reportId]?.title === 'Usage Transfer'
                                    ? 'This report identifies items nearing expiration and lists potential transfer opportunities to adjacent hospitals or departments within your health system.'
                                    : 'N/A'
                        }
                    />

                    {memId && reportContext?.isEditingMemorized ? (
                        <div
                            className="alert py-1 px-2 mb-2"
                            role="status"
                            style={{ backgroundColor: '#057eb6', color: '#fff', borderColor: '#057eb6' }}
                        >
                            <i className="bi bi-pencil-square mr-2" /> Editing memorized report
                        </div>
                    ) : null}

                    <div className="d-flex justify-content-between align-items-center mb-3">

                        <button className="faux-link" onClick={() => reportContext?.setReportNavVisible(!reportContext.reportNavVisible)}>
                            <ArrowLeftCircleFill className="mr-3" /> {reportContext?.reportNavVisible ? 'Hide' : 'Show'} My Report Shelf</button>

                        {isDescription ? <Link to={`${baseUrl}?key=${redisKey}${memId && memId !== 'false' ? `&memid=${memId}` : ''}`} className="btn btn-primary mb-3">
                            ← Back To Report
                        </Link> : <nav className="report-utility-nav mb-3">
                            <Link to={`${baseUrl}/description?key=${redisKey}${memId && memId !== 'false' ? `&memid=${memId}` : ''}`} className="">
                                <FileEarmarkText className="mr-2" />
                                Description
                            </Link>
                            {qSightReportId && qSightUserId && (
                                <PinReport reportId={qSightReportId} userId={qSightUserId} />
                            )}
                            <button
                                className="btn btn-secondary btn-outline"
                                onClick={() => toggleFilter()}
                            >
                                <Funnel className="mr-2" />
                                Filters
                            </button>
                        </nav>
                        }
                    </div>
                    {!isDescription && (<ReportFiltersDisplay reportId={qSightReportId} holderId={qSightHolderId}
                        userId={qSightUserId} memId={memId} memReportResponse={reportResponse?.data?.report} />)}
                    <Row>
                        <Col md={reportContext?.reportNavVisible ? '3' : '0'} className="border-end">
                            {reportContext?.reportNavVisible && (
                                <NavigationPanelMenu reportId={qSightReportId} holderId={qSightHolderId} userId={qSightUserId} reportTitle={reportTitle} />
                            )}

                        </Col>
                        <Col md={reportContext?.reportNavVisible ? '9' : '12'} >
                            {
                                isDescription ?
                                    <>
                                        <DescriptionComponent />
                                    </> : ReportComponent ?
                                        <>
                                            <ReportComponent />
                                        </> :
                                        <div>Report not found</div>
                            }
                            <Outlet />
                        </Col>
                    </Row>
                </>
                :
                <h2>Report Not Found</h2>
            }
        </Container >
    );
};

export default StandardReportLayout;